/**
 * Adapts a raw `.eml` deliverable into the structure the viewer renders.
 *
 * The email text is the deliverable — client-facing output preserved as a record — so inline `cid:`
 * images are substance, not decoration, and are resolved to `data:` URIs rather than dropped. The
 * sanitisation contract is stated as an outcome: the rendered frame must issue no network request
 * for any remote resource and must contain no executable markup.
 */

import PostalMime, { type Address, type Attachment, type Email, type Mailbox } from 'postal-mime';

import { CssQuoteTracker } from './cssQuoteTracker';

/**
 * Byte ceiling above which an `.eml` is offered as a download instead of parsed. Parsing is linear
 * and blocks the main thread: 25 MB measured at ~1,029 ms / 257 MB heap, before `data:` inflation.
 * Checked twice — against the DTO's `fileSizeBytes` before fetching, and against `blob.size` after.
 */
export const EML_MAX_PARSE_BYTES = 10 * 1024 * 1024;

/**
 * The Content-Security-Policy the sanitised document carries in its own `<head>`, as a second layer
 * behind the parse-time strip and behind `sandbox=""` — neither of which it replaces.
 *
 * Every directive is load-bearing. `style-src 'unsafe-inline'` keeps Outlook `MsoNormal` inline styling
 * rendering, which is a shipped requirement; `img-src data:` keeps `cid:`-resolved inline images
 * rendering, which is the substance of the deliverable. `default-src 'none'` refuses everything else.
 */
export const EML_FRAME_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

/** Sources the adapter accepts. `Blob` is what the viewer hands it after fetching. */
export type EmlSource = string | ArrayBuffer | Uint8Array | Blob;

/**
 * One attachment carried by an email deliverable, in the shape the viewer renders.
 *
 * ⚠️ `content` is BYTES, not an object URL, and that is deliberate. Minting a `blob:` URL per
 * attachment at parse time leaks one per attachment for the lifetime of the document unless the
 * viewer revokes every one on unmount — a lifetime with several ways to get it wrong, and the
 * deal-documents viewer already gets it wrong. Creating the URL around the click and revoking it
 * immediately has no lifetime to manage at all.
 */
export interface DeliverableAttachment {
  fileName: string;
  /** Resolved from the file extension; `application/octet-stream` when it cannot be determined. */
  mimeType: string;
  sizeBytes: number;
  content: Uint8Array;
}

export interface ParsedEmlDeliverable {
  from: string; // '' when absent
  to: string; // '' when absent
  cc: string; // '' when absent
  subject: string; // '' when absent
  date: string; // display string; '' when absent or unparseable
  // Sanitised, cid-resolved. Null when there is no HTML part, and null when the HTML part sanitises
  // to structurally-nothing *and* a substantive `bodyText` exists to render instead.
  bodyHtml: string | null;
  bodyText: string | null; // null when there is no text part
  isEmpty: boolean;
  forwardedMessageCount: number; // message/rfc822 parts dropped; 0 when none
  /**
   * Attachments the reader can retrieve, EXCLUDING two kinds that would be duplicates: parts whose
   * content id is `cid:`-referenced by the rendered body (already visible inline) and nested
   * messages (already reported by `forwardedMessageCount`).
   */
  attachments: DeliverableAttachment[];
}

/**
 * Elements removed outright: they execute, navigate, or fetch, and no email body needs them.
 *
 * `svg` and `math` are here because neither is legitimate email content — Outlook does not render SVG
 * and Gmail strips it — and both open foreign-content parsing, behind which `<use>`, `<foreignObject>`,
 * `<feImage>` and an SVG-embedded `<style>` are all further fetching surface.
 *
 * `template` is here for the same reason and one more. It is not email content — no mail client renders
 * it — and its children parse into a detached `DocumentFragment` that `outerHTML` serialises verbatim,
 * so a surviving template carries markup into the frame. There it is re-parsed, and a
 * `shadowrootmode` attribute makes it a *rendering* container via declarative shadow DOM. Removing the
 * container is what keeps that mode out of the frame; `everyElement` reaching the fragment is the layer
 * behind it, not a substitute for it.
 */
const REMOVED_ELEMENTS = ['script', 'link', 'iframe', 'object', 'embed', 'base', 'svg', 'math', 'template'];

/**
 * Attributes that can point at a remote resource and trigger a fetch on load. Matched on
 * `localName`, not `name`: the HTML parser puts `xlink:href` in the XLink namespace, where its `name`
 * is `xlink:href` and only its `localName` is `href`.
 */
const URL_ATTRIBUTES = ['src', 'href', 'srcset', 'background', 'poster'];

/**
 * `url(...)` inside CSS, whether an inline `style` attribute or a `<style>` block.
 *
 * The quoted and unquoted forms are separate alternatives because a **quoted** value may legally
 * contain `)` — `url("a(b).png")` — which one `[^)'"]*` class cannot express. Unquoted values must
 * escape their parentheses, so excluding `)` is correct there and only there.
 */
const CSS_URL_PATTERN = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)'"]*))\s*\)/gi;

/**
 * `@import` with a bare `<string>` target. It fetches exactly as `@import url(...)` does while
 * carrying no `url(` token, so a rule that only rewrites `url(...)` never sees it.
 */
const CSS_IMPORT_STRING_PATTERN = /(@import\s+)("[^"]*"|'[^']*')/gi;

/** A quoted CSS `<string>`, which inside `image-set()` is a URL in its own right. */
const CSS_QUOTED_STRING_PATTERN = /"([^"]*)"|'([^']*)'/g;

/**
 * The `image-set()` function head, including its vendor-prefixed spelling. Matched case-insensitively
 * against the original CSS rather than against a lowercased copy: `toLowerCase()` is not
 * length-preserving — `'İ'` (U+0130) lowercases to two code units — so an offset taken from a lowered
 * copy does not address the same character in the source it is used to slice.
 */
const CSS_IMAGE_SET_HEAD_PATTERN = /(?:-webkit-)?image-set\(/gi;

/** Prefix under which a neutralised reference is parked, so the record still shows it was there. */
const BLOCKED_ATTRIBUTE_PREFIX = 'data-blocked-';

/**
 * Characters replaced when building a parked name out of one the DOM has already refused. Every
 * character this class keeps is a legal XML `NameChar`, so the rebuilt name always survives
 * `setAttribute`. It is deliberately narrower than the `Name` production — it is only ever reached
 * after the DOM has rejected the faithful name, so over-replacing here costs nothing.
 */
const ILLEGAL_ATTRIBUTE_NAME_CHARS = /[^\w.:-]/g;

/**
 * A `data:` URI token. Removed before the protocol-relative test below, because the base64 alphabet
 * includes `/` and a payload may therefore legally contain `//`. The `:` in a scheme cannot appear in
 * base64, so the scheme test needs no such accommodation.
 */
const DATA_URI_TOKEN_PATTERN = /data:[^\s'")]*/gi;

/** An absolute `http:`/`https:` scheme. */
const REMOTE_SCHEME_PATTERN = /https?:/i;

/** A protocol-relative `//` at the start of a value or of a URL token inside one. */
const PROTOCOL_RELATIVE_PATTERN = /(?:^|[\s,;('"=])\/\//;

/**
 * Every element under `root`, including those inside `<template>` content.
 *
 * **This is the single definition of "every element in this document", and it exists because
 * `querySelectorAll` is not one.** A template's children are parsed into a detached
 * `DocumentFragment` hanging off the element, which no selector walk of the tree descends into —
 * while `outerHTML` serialises them verbatim. Every pass here, and the exported audit, once shared
 * that blind spot: a `<template>` body carried `<script>`, `on*` handlers, remote `<img>` and a
 * `<style>` with `url(...)` straight through to the frame, and `findRemoteReferences` called it clean.
 *
 * Recursive, because a template nests inside another template's fragment and one level of reach would
 * leave the inner one exactly as unreachable as before.
 * @param {Document | DocumentFragment} root - The document, or a template's content fragment.
 * @returns {Element[]} Every descendant element, fragment contents included, in document order.
 */
function everyElement(root: Document | DocumentFragment): Element[] {
  const found: Element[] = [];
  for (const element of Array.from(root.querySelectorAll('*'))) {
    found.push(element);
    if (element instanceof HTMLTemplateElement) found.push(...everyElement(element.content));
  }
  return found;
}

/**
 * Every `<style>` block under `root`, template content included, so the two CSS passes and the audit
 * read the same set of blocks as each other and as `everyElement`.
 * @param {Document | DocumentFragment} root - The document, or a template's content fragment.
 * @returns {HTMLStyleElement[]} Every style element, in document order.
 */
function everyStyleElement(root: Document | DocumentFragment): HTMLStyleElement[] {
  return everyElement(root).filter((element): element is HTMLStyleElement => element instanceof HTMLStyleElement);
}

/**
 * Reports whether a value still carries a reference the frame could fetch over the network.
 *
 * This is the predicate behind the contract OPERATIVE §5 states as an outcome, and it is deliberately
 * narrow: `http:`, `https:` and protocol-relative `//`. `isAllowedUrl`'s allowlist remains the primary
 * rule and is stricter — it admits only `data:` and pure `#` fragments — so a scheme such as `ftp:` is
 * neutralised by the primary pass and not by this backstop.
 * @param {string} value - An attribute value or a run of CSS text.
 * @returns {boolean} True when a remote reference survives in it.
 */
function containsRemoteReference(value: string): boolean {
  const withoutDataUris = value.replace(DATA_URI_TOKEN_PATTERN, '');
  return REMOTE_SCHEME_PATTERN.test(withoutDataUris) || PROTOCOL_RELATIVE_PATTERN.test(withoutDataUris);
}

/**
 * Audits serialised sanitiser output against the invariant: no `http:`, `https:` or protocol-relative
 * `//` reference survives in any **attribute value** or any **CSS text**.
 *
 * **Text nodes are exempt.** An analyst legitimately typing a URL in the message body is not a fetch,
 * and blocking it would corrupt the record. `data-blocked-*` attributes are exempt too — that is where
 * neutralised references are deliberately parked.
 *
 * Exported so the invariant can be watched failing: a fail-closed detector nobody has seen fail is
 * untested.
 * @param {string} html - Serialised HTML, normally `ParsedEmlDeliverable.bodyHtml`.
 * @returns {string[]} One entry per surviving reference; empty when the output satisfies the invariant.
 */
export function findRemoteReferences(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const found: string[] = [];
  for (const element of everyElement(doc)) {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      if (name.startsWith(BLOCKED_ATTRIBUTE_PREFIX) || !containsRemoteReference(attribute.value)) continue;
      found.push(`${element.tagName.toLowerCase()}[${name}]=${attribute.value.trim()}`);
    }
  }
  for (const style of everyStyleElement(doc)) {
    const css = style.textContent ?? '';
    if (containsRemoteReference(css)) found.push(`style{}=${css.trim()}`);
  }
  return found;
}

/**
 * Formats a single mailbox as `Name <address>`, falling back to whichever half is present.
 * @param {Mailbox} mailbox - The parsed mailbox.
 * @returns {string} The display string, or '' when the mailbox carries neither part.
 */
function formatMailbox(mailbox: Mailbox): string {
  const name = mailbox.name?.trim() ?? '';
  const address = mailbox.address?.trim() ?? '';
  if (name !== '' && address !== '') return `${name} <${address}>`;
  return name !== '' ? name : address;
}

/**
 * Formats one address, which RFC 5322 allows to be a named group of mailboxes.
 * @param {Address} address - The parsed address or group.
 * @returns {string} The display string.
 */
function formatAddress(address: Address): string {
  if (address.group !== undefined) {
    const members = address.group.map(formatMailbox).filter((member) => member !== '');
    const label = address.name?.trim() ?? '';
    const joined = members.join(', ');
    return label !== '' ? `${label}: ${joined}` : joined;
  }
  return formatMailbox(address);
}

/**
 * Formats a header that carries a list of addresses, such as `To` or `Cc`.
 * @param {Address[] | undefined} addresses - The parsed addresses, absent when the header is.
 * @returns {string} The comma-joined display string, or '' when there are none.
 */
function formatAddressList(addresses: Address[] | undefined): string {
  if (addresses == null) return '';
  return addresses
    .map(formatAddress)
    .filter((formatted) => formatted !== '')
    .join(', ');
}

/**
 * Reads the original `Date:` header rather than the parser's normalised value.
 *
 * `postal-mime` converts a parseable date to `toISOString()`, which is UTC. This is an evidentiary
 * record: a message sent 23:00 America/New_York rendered as the next calendar day is a wrong fact,
 * so the source header's own UTC offset is preserved verbatim.
 * @param {Email} email - The parsed email.
 * @returns {string} The raw header value, or '' when absent or unparseable.
 */
function resolveDisplayDate(email: Email): string {
  const raw = email.headers.find((header) => header.key === 'date')?.value?.trim() ?? '';
  if (raw === '') return '';
  return Number.isNaN(new Date(raw).getTime()) ? '' : raw;
}

/**
 * Maps each `Content-ID` to a `data:` URI. Keyed off the presence of a content id alone: a part can
 * be both `Content-Disposition: attachment` and `cid:`-referenced, so disposition cannot classify it.
 * @param {Attachment[]} attachments - The parsed attachments.
 * @returns {Map<string, string>} Content id (angle brackets stripped) to `data:` URI.
 */
function buildCidMap(attachments: Attachment[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const attachment of attachments) {
    const contentId = attachment.contentId?.replace(/^<|>$/g, '').trim() ?? '';
    // `content` is a string because the parser is asked for base64; the type permits buffers too.
    if (contentId === '' || typeof attachment.content !== 'string') continue;
    map.set(contentId, `data:${attachment.mimeType};base64,${attachment.content}`);
  }
  return map;
}

/**
 * Decides whether a URL may stay in the rendered frame. Only `data:` URIs and pure fragments can,
 * because neither issues a network request.
 * @param {string} value - The raw attribute or `url(...)` value.
 * @returns {boolean} True when the value is safe to keep.
 */
function isAllowedUrl(value: string): boolean {
  const trimmed = value.trim();
  return /^data:/i.test(trimmed) || trimmed.startsWith('#');
}

/**
 * Rewrites non-`data:` `url(...)` values to `none`, leaving the surrounding CSS intact.
 * @param {string} css - The CSS text.
 * @returns {string} The CSS with `url(...)` references neutralised.
 */
function neutraliseCssUrlFunctions(css: string): string {
  return css.replace(
    CSS_URL_PATTERN,
    (match: string, doubleQuoted?: string, singleQuoted?: string, unquoted?: string) =>
      isAllowedUrl(doubleQuoted ?? singleQuoted ?? unquoted ?? '') ? match : 'none'
  );
}

/**
 * Rewrites an `@import` whose target is a bare `<string>` to `@import none`, which is the same
 * treatment the `url(...)` form already gets and is an invalid at-rule a CSS parser then discards.
 * @param {string} css - The CSS text.
 * @returns {string} The CSS with bare-string imports neutralised.
 */
function neutraliseCssImports(css: string): string {
  return css.replace(CSS_IMPORT_STRING_PATTERN, (match: string, keyword: string, quoted: string) =>
    isAllowedUrl(quoted.slice(1, -1)) ? match : `${keyword}none`
  );
}

/**
 * Finds the index of the `)` closing the `(` at `openIndex`, tracking nesting and quoted strings.
 *
 * The quoted-string half is `CssQuoteTracker`'s; this function owns only the depth count. A `)` inside
 * a string does not close the function — `url("a(b).png")` is one legal value — which is why the walk
 * cannot simply count parentheses.
 * @param {string} css - The CSS text.
 * @param {number} openIndex - Index of the opening parenthesis.
 * @returns {number} The closing index, or -1 when the function is unterminated.
 */
function findMatchingParenthesis(css: string, openIndex: number): number {
  const strings = new CssQuoteTracker();
  let depth = 0;
  for (let index = openIndex; index < css.length; index += 1) {
    const char = css[index];
    if (!strings.accept(char)) continue;
    if (char === '(') depth += 1;
    else if (char === ')') {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
}

/**
 * Locates the next `image-set()` head at or after `from`, in either spelling. The returned offsets
 * index `css` itself, so a caller may slice the same string it passed in.
 * @param {string} css - The CSS text.
 * @param {number} from - Index to search from.
 * @returns {{ start: number; open: number } | null} The head's start and its `(` index, or null.
 */
function findImageSetHead(css: string, from: number): { start: number; open: number } | null {
  CSS_IMAGE_SET_HEAD_PATTERN.lastIndex = from;
  const head = CSS_IMAGE_SET_HEAD_PATTERN.exec(css);
  return head === null ? null : { start: head.index, open: head.index + head[0].length - 1 };
}

/**
 * Reports whether every candidate in an `image-set()` list is an allowed URL. Candidates may be bare
 * `<string>`s as well as `url(...)` values, and a bare string there is a URL.
 * @param {string} inner - The text between the function's parentheses.
 * @returns {boolean} True when the whole list is safe to keep.
 */
function imageSetIsAllowed(inner: string): boolean {
  for (const match of inner.matchAll(CSS_URL_PATTERN)) {
    if (!isAllowedUrl(match[1] ?? match[2] ?? match[3] ?? '')) return false;
  }
  for (const match of inner.replace(CSS_URL_PATTERN, '').matchAll(CSS_QUOTED_STRING_PATTERN)) {
    if (!isAllowedUrl(match[1] ?? match[2] ?? '')) return false;
  }
  return true;
}

/**
 * Rewrites an `image-set()` carrying any disallowed candidate to `none`, the same treatment `url(...)`
 * gets. A list whose every candidate is a `data:` URI is left exactly as it is.
 * @param {string} css - The CSS text.
 * @returns {string} The CSS with remote `image-set()` functions neutralised.
 */
function neutraliseCssImageSets(css: string): string {
  let result = '';
  let cursor = 0;
  for (let head = findImageSetHead(css, cursor); head !== null; head = findImageSetHead(css, cursor)) {
    const close = findMatchingParenthesis(css, head.open);
    if (close < 0) break;
    result += css.slice(cursor, head.start);
    result += imageSetIsAllowed(css.slice(head.open + 1, close)) ? css.slice(head.start, close + 1) : 'none';
    cursor = close + 1;
  }
  return result + css.slice(cursor);
}

/**
 * Neutralises every remote reference form CSS can carry: `image-set()` candidate lists, bare-string
 * `@import` targets, and `url(...)` values. Ordered so `image-set()` is judged as a whole before its
 * inner `url(...)` values are rewritten individually.
 * @param {string} css - The CSS text.
 * @returns {string} The CSS with remote references neutralised.
 */
function neutraliseCssUrls(css: string): string {
  return neutraliseCssUrlFunctions(neutraliseCssImports(neutraliseCssImageSets(css)));
}

/**
 * Removes executable and fetching elements, plus `<meta http-equiv="refresh">`.
 *
 * One walk rather than two selector queries, so both rules read the same element set — the one
 * `everyElement` defines. Removing a `<template>` detaches its whole fragment, so a later `.remove()`
 * on something that was inside it is a no-op and the walk needs no ordering care.
 * @param {Document} doc - The detached rewrite document.
 * @returns {void}
 */
function stripDangerousElements(doc: Document): void {
  for (const element of everyElement(doc)) {
    const tagName = element.tagName.toLowerCase();
    if (REMOVED_ELEMENTS.includes(tagName)) {
      element.remove();
      continue;
    }
    if (tagName === 'meta' && (element.getAttribute('http-equiv') ?? '').trim().toLowerCase() === 'refresh') {
      element.remove();
    }
  }
}

/**
 * Resolves `cid:` image references against the parsed parts. An `<img>` whose reference has no
 * matching part is removed, so the body reads clean-but-imageless rather than visibly broken.
 * @param {Document} doc - The detached rewrite document.
 * @param {Map<string, string>} cidMap - Content id to `data:` URI.
 * @returns {void}
 */
function resolveCidImages(doc: Document, cidMap: Map<string, string>): void {
  for (const image of Array.from(doc.querySelectorAll('img'))) {
    const source = image.getAttribute('src')?.trim() ?? '';
    if (!/^cid:/i.test(source)) continue;
    const contentId = source.slice('cid:'.length).replace(/^<|>$/g, '').trim();
    const resolved = cidMap.get(contentId);
    if (resolved == null) {
      image.remove();
      continue;
    }
    image.setAttribute('src', resolved);
  }
}

/**
 * Extracts the URL portion of each `srcset` candidate, following the HTML srcset rules rather than
 * splitting naively on commas.
 *
 * A base64 `data:` URI legally contains a comma with no following whitespace, so `split(',')` would
 * tear one candidate into two and block a legitimate list. The rules instead say a candidate's URL
 * runs to the first whitespace; a URL ending in commas has them stripped and carries no descriptor;
 * and otherwise the descriptor runs to the next top-level comma.
 * @param {string} value - The raw `srcset` attribute value.
 * @returns {string[]} One URL per candidate, in source order.
 */
function extractSrcsetUrls(value: string): string[] {
  const urls: string[] = [];
  let index = 0;
  while (index < value.length) {
    while (index < value.length && (/\s/.test(value[index]) || value[index] === ',')) index += 1;
    const start = index;
    while (index < value.length && !/\s/.test(value[index])) index += 1;
    const url = value.slice(start, index);
    if (url === '') break;
    urls.push(url.endsWith(',') ? url.replace(/,+$/, '') : url);
    if (url.endsWith(',')) continue;
    let depth = 0;
    while (index < value.length) {
      const char = value[index];
      index += 1;
      if (char === '(') depth += 1;
      else if (char === ')') depth = Math.max(0, depth - 1);
      else if (char === ',' && depth === 0) break;
    }
  }
  return urls;
}

/**
 * Decides whether an enumerated URL attribute's value may stay. `srcset` carries a candidate list, so
 * each candidate is validated independently and one failure neutralises the whole attribute.
 * @param {string} localName - The attribute's local name, lowercased.
 * @param {string} value - The raw attribute value.
 * @returns {boolean} True when the value is safe to keep.
 */
function isAttributeValueAllowed(localName: string, value: string): boolean {
  if (localName !== 'srcset') return isAllowedUrl(value);
  const urls = extractSrcsetUrls(value);
  return urls.length > 0 && urls.every(isAllowedUrl);
}

/**
 * Removes an attribute carrying a remote reference and parks its value under `data-blocked-*`.
 *
 * **The removal comes first and cannot fail**, because parking the evidence can. The parked name is
 * built from the author's own attribute name, and the HTML tokenizer ends a name only at whitespace,
 * `/`, `=` or `>` — so `<div foo[bar]="https://…">` parses, while `setAttribute` refuses
 * `data-blocked-foo[bar]` with `InvalidCharacterError` because `[` is illegal in the XML `Name`
 * production. Ordered the other way, and unguarded, that throw leaves the reference in place and
 * propagates out of `parseEmlDeliverable`: the viewer renders its error state instead of the email, on
 * this and every later attempt, so a sender-chosen attribute name permanently bricks the deliverable.
 *
 * **The DOM adjudicates name validity, not a pattern.** `setAttribute('data-blocked-données', …)`
 * succeeds — non-ASCII letters are legal `NameChar`s — so a hand-rolled ASCII test would silently
 * discard evidence for names the DOM would have taken. The faithful name is attempted, and only a
 * refusal falls back to a rebuilt one.
 * @param {Element} element - The element carrying the attribute.
 * @param {Attr} attribute - The attribute whose value is being neutralised.
 * @returns {void}
 */
function parkBlockedAttribute(element: Element, attribute: Attr): void {
  const localName = attribute.localName.toLowerCase();
  element.removeAttribute(attribute.name);
  try {
    element.setAttribute(`${BLOCKED_ATTRIBUTE_PREFIX}${localName}`, attribute.value);
  } catch {
    // Rebuilt from legal `NameChar`s only, so this call cannot be refused in turn. Two exotic names on
    // one element can collapse to the same parked name, in which case the later value wins; the
    // removal above is unaffected, so the contract holds and only one name's evidence is coarsened.
    const parked = `${BLOCKED_ATTRIBUTE_PREFIX}${localName.replace(ILLEGAL_ATTRIBUTE_NAME_CHARS, '-')}`;
    element.setAttribute(parked, attribute.value);
  }
}

/**
 * Strips `on*` handlers and rewrites remote URL attributes to `data-blocked-*`, so the evidentiary
 * record still shows a remote reference was there. Also neutralises `url(...)` in `style`
 * attributes and `<style>` blocks.
 * @param {Document} doc - The detached rewrite document.
 * @returns {void}
 */
function neutraliseAttributes(doc: Document): void {
  for (const element of everyElement(doc)) {
    for (const attribute of Array.from(element.attributes)) {
      // Matched on `localName` so a namespaced form such as `xlink:href` is caught alongside `href`.
      const localName = attribute.localName.toLowerCase();
      if (attribute.name.toLowerCase().startsWith('on')) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if (localName === 'style') {
        element.setAttribute(attribute.name, neutraliseCssUrls(attribute.value));
        continue;
      }
      if (!URL_ATTRIBUTES.includes(localName) || isAttributeValueAllowed(localName, attribute.value)) continue;
      parkBlockedAttribute(element, attribute);
    }
  }
  for (const style of everyStyleElement(doc)) {
    style.textContent = neutraliseCssUrls(style.textContent ?? '');
  }
}

/**
 * Drops any CSS statement — a declaration or an at-rule — that still carries a remote reference.
 *
 * The unit is the statement rather than the whole block, so a sibling declaration and the rest of the
 * stylesheet survive. The quoted-string half of the walk is `CssQuoteTracker`'s, so a `;` inside a
 * string — legal in a URL path — is not a boundary; this function owns only the accumulation.
 *
 * `accept` is on the left of the `&&` so it runs for every character regardless of the boundary test,
 * which is what keeps the tracker's state in step with the walk.
 * @param {string} css - The CSS text.
 * @returns {string} The CSS with offending statements removed.
 */
function dropRemoteCssStatements(css: string): string {
  const strings = new CssQuoteTracker();
  let result = '';
  let statement = '';
  for (const char of css) {
    if (strings.accept(char) && (char === ';' || char === '{' || char === '}')) {
      result += (containsRemoteReference(statement) ? '' : statement) + char;
      statement = '';
      continue;
    }
    statement += char;
  }
  return result + (containsRemoteReference(statement) ? '' : statement);
}

/**
 * The fail-closed backstop for the invariant `findRemoteReferences` audits: every remote reference
 * still present in an attribute value or in CSS text is neutralised rather than emitted.
 *
 * This runs on the document that is about to be serialised rather than on the serialised string,
 * because neutralising *the whole enclosing attribute or declaration* needs attribute boundaries — the
 * same reason trap 2 prohibits regex-rewriting whole HTML. Text nodes are exempt; a URL an analyst
 * typed is not a fetch and blocking it would corrupt the record. `data-blocked-*` is exempt because
 * that is where neutralised references are deliberately parked.
 *
 * It is a backstop, not the fix. Every form the primary pass enumerates is corrected at its source, so
 * on a body the primary pass handled this pass changes nothing.
 * @param {Document} doc - The sanitised rewrite document.
 * @returns {void}
 */
function enforceNoRemoteReferences(doc: Document): void {
  for (const element of everyElement(doc)) {
    for (const attribute of Array.from(element.attributes)) {
      const localName = attribute.localName.toLowerCase();
      if (attribute.name.toLowerCase().startsWith(BLOCKED_ATTRIBUTE_PREFIX)) continue;
      if (localName === 'style') {
        element.setAttribute(attribute.name, dropRemoteCssStatements(attribute.value));
        continue;
      }
      if (!containsRemoteReference(attribute.value)) continue;
      parkBlockedAttribute(element, attribute);
    }
  }
  for (const style of everyStyleElement(doc)) {
    style.textContent = dropRemoteCssStatements(style.textContent ?? '');
  }
}

/**
 * Adds the frame's own Content-Security-Policy as the first thing in `<head>`.
 *
 * A second layer, not a replacement for either the parse-time strip or `sandbox=""`. `sandbox=""`
 * gives the frame an opaque origin and a meta CSP applies to that document, so a remote reference in
 * a form nobody enumerated is refused by the browser even if it reaches the frame.
 *
 * `style-src 'unsafe-inline'` and `img-src data:` are both load-bearing requirements, not slack:
 * Outlook `MsoNormal` inline styling and `cid:`-resolved images are shipped behaviour.
 * @param {Document} doc - The sanitised rewrite document.
 * @returns {void}
 */
function injectContentSecurityPolicy(doc: Document): void {
  const meta = doc.createElement('meta');
  meta.setAttribute('http-equiv', 'Content-Security-Policy');
  meta.setAttribute('content', EML_FRAME_CSP);
  doc.head.prepend(meta);
}

/**
 * Reports whether a sanitised body has neither text nor any element left to render.
 * @param {Document} doc - The sanitised rewrite document.
 * @returns {boolean} True when nothing remains.
 */
function isBlankBody(doc: Document): boolean {
  const text = doc.body?.textContent?.trim() ?? '';
  if (text !== '') return false;
  return doc.body?.querySelector('*') == null;
}

export interface SanitisedBody {
  html: string;
  isBlank: boolean;
}

/**
 * Sanitises an HTML body and resolves its inline images.
 *
 * The rewrite document comes from `DOMParser`, which has no browsing context and therefore fetches
 * nothing. Assigning the same HTML to a live element's `innerHTML` would issue a request for every
 * remote `<img>` in the parent origin — the exact leak the remote-stripping rule exists to prevent.
 *
 * ⚠️ EXPORTED FOR THE `.msg` PATH, WHICH IS NOW A SECOND CALLER. `msgAdapter` recovers HTML out of
 * Outlook's compressed RTF, and that HTML is exactly as untrusted as an `.eml` body. It routes
 * through THIS function rather than a copy, so the two formats cannot drift apart in what they
 * defend against. ⛔ Do not add a second sanitiser, and do not let any caller reach the viewer's
 * frame without passing through here.
 *
 * `cidMap` is built per-format rather than here: `postal-mime` and `@npeersab/msgreader` describe
 * attachments with different shapes, so each adapter maps its own into this shared contract.
 * @param {string} html - The raw HTML body.
 * @param {Map<string, string>} cidMap - Content id to `data:` URI.
 * @returns {SanitisedBody} The serialised safe HTML and whether anything is left to render.
 */
export function sanitiseBodyHtml(html: string, cidMap: Map<string, string>): SanitisedBody {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  stripDangerousElements(doc);
  // Before neutralisation: a `cid:` reference is not a `data:` URI and would otherwise be blocked.
  resolveCidImages(doc, cidMap);
  neutraliseAttributes(doc);
  // Injected before the invariant is enforced, so the policy itself is covered by it.
  injectContentSecurityPolicy(doc);
  // Last, so it sees exactly what is about to be emitted. Quiet on anything the passes above handled.
  enforceNoRemoteReferences(doc);
  // The whole document, not just the body — Outlook keeps its `MsoNormal` rules in a head `<style>`.
  return { html: doc.documentElement.outerHTML, isBlank: isBlankBody(doc) };
}

/**
 * Decodes a base64 attachment payload to bytes.
 * @param {string} base64 - The payload, as `attachmentEncoding: 'base64'` produced it.
 * @returns {Uint8Array} The decoded bytes; empty when the payload will not decode.
 */
function decodeBase64(base64: string): Uint8Array {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return new Uint8Array(0);
  }
}

/**
 * Selects the attachments worth offering the reader.
 *
 * Two kinds are excluded because listing them would duplicate something already on screen: parts
 * carrying a `contentId`, which `buildCidMap` has already resolved into the body as a `data:` URI,
 * and nested messages, which `forwardedMessageCount` already reports.
 * @param {Attachment[]} attachments - Every part postal-mime treated as an attachment.
 * @returns {DeliverableAttachment[]} The attachments to offer, in message order.
 */
function collectEmlAttachments(attachments: Attachment[]): DeliverableAttachment[] {
  return attachments
    .filter((part) => part.mimeType !== 'message/rfc822')
    .filter((part) => (part.contentId ?? '').trim() === '')
    .map((part) => {
      const content = typeof part.content === 'string' ? decodeBase64(part.content) : new Uint8Array(0);
      return {
        fileName: part.filename ?? '',
        mimeType: part.mimeType ?? 'application/octet-stream',
        sizeBytes: content.length,
        content
      };
    });
}

/**
 * Parses a raw `.eml` deliverable into the structure the viewer renders.
 *
 * Nested `message/rfc822` parts are not recursed into; `forwardedMessageCount` carries how many
 * were dropped so the viewer can surface a marker.
 *
 * ⚠️ ATTACHMENTS ARE NOW SURFACED, REVERSING AN EARLIER DECISION. This adapter used to discard them
 * on the grounds that "an attachment the analyst sent is uploaded as its own deliverable" — which
 * follows from ADR-0003 Decision 2 and is true of files an analyst uploads. It is NOT true of files
 * embedded inside an uploaded message: those were never uploaded separately, so no deliverable row
 * exists for them and they were unreachable through any surface. Decision 2 governs the storage
 * model, not what the viewer may show from inside a file it already has.
 * @param {EmlSource} source - The raw message, typically a `Blob` fetched from storage.
 * @returns {Promise<ParsedEmlDeliverable>} The adapted deliverable.
 */
export async function parseEmlDeliverable(source: EmlSource): Promise<ParsedEmlDeliverable> {
  const email = await PostalMime.parse(source, {
    // Every nested message becomes an attachment rather than being parsed inline.
    forceRfc822Attachments: true,
    attachmentEncoding: 'base64'
  });

  const sanitised = email.html == null ? null : sanitiseBodyHtml(email.html, buildCidMap(email.attachments));
  const bodyText = email.text ?? null;
  const from = email.from == null ? '' : formatAddress(email.from);
  const subject = email.subject ?? '';

  const htmlIsBlank = sanitised == null || sanitised.isBlank;
  const textIsBlank = (bodyText ?? '').trim() === '';

  // The HTML sanitised to structurally-nothing and there is a substantive text alternative to render
  // instead, so `bodyHtml` is nulled and the viewer's `bodyText` branch fires rather than a blank
  // frame. The two parts of a `multipart/alternative` are equivalent representations of the same
  // message, so nothing is lost and no marker is warranted.
  //
  // **Conditional on the text being substantive, deliberately.** A body whose only content was a
  // remote `<img>` with no text alternative keeps its non-null `bodyHtml`, renders a blank frame, and
  // does not trip `isEmpty`: the headers are the record and `data-blocked-*` preserves the evidence
  // that an image was there. Nulling unconditionally would erase that (OPERATIVE §5).
  const bodyHtml = sanitised == null || (sanitised.isBlank && !textIsBlank) ? null : sanitised.html;

  return {
    from,
    to: formatAddressList(email.to),
    cc: formatAddressList(email.cc),
    subject,
    date: resolveDisplayDate(email),
    bodyHtml,
    bodyText,
    // Must not fire on a real message that merely has no body — such a message still has a `From`.
    isEmpty: htmlIsBlank && textIsBlank && from.trim() === '' && subject.trim() === '',
    forwardedMessageCount: email.attachments.filter((part) => part.mimeType === 'message/rfc822').length,
    attachments: collectEmlAttachments(email.attachments)
  };
}
